import { motion } from 'framer-motion';
import { ShieldCheck, Scale, Globe2, Stethoscope } from 'lucide-react';
import { SceneFrame } from './_atoms';

const standards = [
  {
    Icon: Scale,        title: 'ISO/IEC 27001:2022',  subtitle: 'Information Security Management',
    color: 'from-indigo-500 to-indigo-700',
    controls: ['A.5.15 Access Control', 'A.5.16 Identity Management', 'A.5.18 Access Rights', 'A.8.2 Privileged Access']
  },
  {
    Icon: ShieldCheck,  title: 'NIST SP 800-53 Rev. 5', subtitle: 'Security & Privacy Controls',
    color: 'from-teal-500 to-teal-700',
    controls: ['AC-2 Account Management', 'AC-3 Access Enforcement', 'AC-5 Separation of Duties', 'AC-6 Least Privilege']
  },
  {
    Icon: Stethoscope,  title: 'HIPAA § 164.312',      subtitle: 'Technical Safeguards',
    color: 'from-rose-500 to-rose-700',
    controls: ['Unique User ID', 'Minimum Necessary', 'Audit Controls', 'Integrity Controls']
  },
  {
    Icon: Globe2,       title: 'Industry Best Practices', subtitle: 'OWASP · AWS · WorkOS · OpenFGA',
    color: 'from-amber-500 to-amber-700',
    controls: ['Tenant-scoped roles', 'Role catalog per tenant type', 'Category isolation', 'Auditability by design']
  },
];

export default function ArchStandardsScene() {
  return (
    <SceneFrame
      eyebrow="Standards & best practices"
      title="Compliance by design"
      subtitle="Every architectural decision maps to a recognised standard or principle — ISO 27001, NIST 800-53, HIPAA, and industry best practices for multi-tenant access control."
    >
      <div className="grid grid-cols-2 gap-5 mt-4">
        {standards.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 14, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.3 + i * 0.18, duration: 0.5 }}
            className="relative rounded-2xl bg-white border border-[#E5E7EB] p-5 shadow-sm overflow-hidden"
          >
            <div className={`absolute top-0 right-0 w-40 h-40 rounded-full bg-gradient-to-br ${s.color} opacity-10 blur-3xl`} />
            <div className={`relative w-11 h-11 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-md mb-3`}>
              <s.Icon size={19} className="text-white" />
            </div>
            <h3 className="relative font-heading font-extrabold text-[16px] text-[#0A0A0A]">{s.title}</h3>
            <p className="relative font-body text-[12px] text-[#6B7280] mb-3">{s.subtitle}</p>
            <div className="relative flex flex-wrap gap-1.5">
              {s.controls.map((c, j) => (
                <motion.span
                  key={c}
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + i * 0.18 + j * 0.06 }}
                  className="px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-[11px] font-body font-medium text-slate-700"
                >{c}</motion.span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }}
        className="mt-6 rounded-2xl bg-gradient-to-r from-teal-600 to-indigo-600 text-white px-6 py-4 flex items-center gap-4 shadow-lg"
      >
        <ShieldCheck size={26} className="text-white flex-shrink-0" />
        <p className="font-body text-[13px] leading-relaxed">
          <span className="font-bold">Every design decision has a corresponding justification</span> rooted in a recognised standard — from ISO 27001 access control to the NIST RBAC formal model (ANSI/INCITS 359).
        </p>
      </motion.div>
    </SceneFrame>
  );
}
